import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Key, Trash2, X } from "lucide-react";
import toast from "react-hot-toast";
import { apiService } from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";
import Pagination from "../components/Pagination";
import type { DtAPIKey, DtAPIKeyPrefix, DtAPIKeyListResponse } from "../types";

export default function DtApiKeysPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { data: response, isLoading } = useQuery(
    ["dt-api-keys", search, page, pageSize],
    () =>
      apiService.getDtAPIKeys({
        search: search || undefined,
        page,
        limit: pageSize,
      }),
  );

  const data = response?.data?.data as DtAPIKeyListResponse | undefined;

  const deleteMutation = useMutation(
    (id: string) => apiService.deleteDtAPIKey(id),
    {
      onSuccess: () => {
        toast.success("API key deleted");
        queryClient.invalidateQueries(["dt-api-keys"]);
      },
      onError: (_error: any) => {
        toast.error("Failed to delete API key");
      },
    },
  );

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          DT API Keys
        </h1>
        <button
          onClick={() => navigate("/dt-api-keys/create")}
          className="btn btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create API Key
        </button>
      </div>

      <div className="flex items-center gap-4">
        <input
          type="text"
          placeholder="Search by owner..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="input max-w-sm"
        />
      </div>

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Owner
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                API Key
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Prefixes
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Key Owners
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {data?.items?.map((key: DtAPIKey) => (
              <DtAPIKeyRow
                key={key.id}
                apiKey={key}
                onDelete={() => deleteMutation.mutate(key.id)}
              />
            ))}
            {(!data?.items || data.items.length === 0) && (
              <tr>
                <td
                  colSpan={7}
                  className="px-6 py-8 text-center text-gray-500 dark:text-gray-400"
                >
                  No API keys found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {data && data.totalPages > 1 && (
        <Pagination
          currentPage={page}
          totalPages={data.totalPages}
          itemsPerPage={pageSize}
          totalItems={Number(data.total)}
          onPageChange={setPage}
          onItemsPerPageChange={(size: number) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      )}
    </div>
  );
}

interface DtAPIKeyRowProps {
  apiKey: DtAPIKey;
  onDelete: () => void;
}

function DtAPIKeyRow({ apiKey, onDelete }: DtAPIKeyRowProps) {
  const [showPrefixes, setShowPrefixes] = useState(false);

  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-750">
      <td className="px-6 py-4 whitespace-nowrap">
        <Link
          to={`/dt-api-keys/${apiKey.id}/edit`}
          className="flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:underline"
        >
          <Key className="h-4 w-4" />
          {apiKey.owner}
        </Link>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {apiKey.rawKey ? (
          <code className="text-xs font-mono text-gray-600 dark:text-gray-300">
            {apiKey.rawKey}
          </code>
        ) : (
          <span className="text-xs text-gray-400 dark:text-gray-500 italic">
            Hidden
          </span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            apiKey.isActive
              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
              : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
          }`}
        >
          {apiKey.isActive ? "Active" : "Inactive"}
        </span>
      </td>
      <td className="px-6 py-4">
        <PrefixesCell
          prefixes={apiKey.prefixes}
          isOpen={showPrefixes}
          onToggle={() => setShowPrefixes(!showPrefixes)}
          onClose={() => setShowPrefixes(false)}
        />
      </td>
      <td className="px-6 py-4">
        <div className="flex flex-wrap gap-1">
          {apiKey.owners?.map((o) => (
            <span
              key={o.id}
              className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
            >
              {o.user?.name || o.userId}
            </span>
          ))}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
        {new Date(apiKey.createdAt).toLocaleDateString()}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right">
        <button
          onClick={onDelete}
          className="btn-icon text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
          title="Delete"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );
}

interface PrefixesCellProps {
  prefixes: DtAPIKeyPrefix[] | undefined;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}

function PrefixesCell({
  prefixes,
  isOpen,
  onToggle,
  onClose,
}: PrefixesCellProps) {
  if (!prefixes || prefixes.length === 0) {
    return (
      <span className="text-xs text-gray-400 dark:text-gray-500 italic">
        Unrestricted
      </span>
    );
  }

  if (prefixes.length === 1) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
        {prefixes[0]?.allowedPrefix}
      </span>
    );
  }

  return (
    <>
      <button
        onClick={onToggle}
        className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 cursor-pointer"
      >
        {prefixes.length} prefixes
      </button>

      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={onClose}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Allowed Prefixes ({prefixes.length})
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <ul className="space-y-2">
              {prefixes.map((prefix) => (
                <li
                  key={prefix.id}
                  className="px-3 py-2 rounded bg-gray-50 dark:bg-gray-900 text-sm text-gray-700 dark:text-gray-300 font-mono"
                >
                  {prefix.allowedPrefix}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  );
}
